import React, { useState, useEffect } from "react";
import { X, Search, Loader2, Sliders } from "lucide-react";
import {
  ComponentCategory,
  IProduct,
  SelectedFilters,
  IFilterSet,
} from "../types";
import { apiService } from "../services/api";
import { formatPrice } from "../utils/price";
import { useProductFilters } from "../hooks/useProductFilters";
import { buildFilterParams } from "../utils/getProductsParams";
import { FilterGroup } from "./FilterGroup";

// Skeleton Loading Component
const SkeletonBox: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div
    className={`srb-rounded ${className}`}
    style={{ backgroundColor: "var(--srb-border-subtle)" }}
  />
);

const ProductSkeleton: React.FC = () => (
  <div className="srb-w-full srb-p-4 srb-bg-background srb-border srb-border-secondary srb-animate-pulse">
    <div className="srb-flex srb-gap-4">
      {/* Image skeleton */}
      <SkeletonBox className="srb-w-20 srb-h-20 srb-flex-shrink-0" />

      {/* Content skeleton */}
      <div className="srb-flex-1 srb-min-w-0 srb-space-y-2">
        {/* Title */}
        <SkeletonBox className="srb-h-5 srb-w-3/4" />
        {/* Brand */}
        <SkeletonBox className="srb-h-4 srb-w-1/4" />
        {/* Description */}
        <SkeletonBox className="srb-h-3 srb-w-full srb-mt-2" />
        <SkeletonBox className="srb-h-3 srb-w-2/3" />
      </div>

      {/* Price skeleton */}
      <div className="srb-flex srb-flex-col srb-items-end srb-justify-center srb-flex-shrink-0 srb-space-y-2">
        <SkeletonBox className="srb-h-6 srb-w-20" />
        <SkeletonBox className="srb-h-3 srb-w-16" />
      </div>
    </div>
  </div>
);

const ProductSkeletonList: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="srb-space-y-3">
    {Array.from({ length: count }).map((_, index) => (
      <ProductSkeleton key={index} />
    ))}
  </div>
);

// Filter Modal Component
interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterSets: IFilterSet[];
  isLoading: boolean;
  selectedFilters: SelectedFilters;
  onSelectFilter: (title: string, value: string) => void;
  onRangeChange: (title: string, min: string, max: string) => void;
  onClearAll: () => void;
  activeFilterCount: number;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filterSets,
  isLoading,
  selectedFilters,
  onSelectFilter,
  onRangeChange,
  onClearAll,
  activeFilterCount,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="srb-fixed srb-inset-0 srb-flex srb-items-center srb-justify-center srb-p-4"
      style={{ zIndex: 10001 }}
    >
      <div
        className="srb-absolute srb-inset-0"
        style={{ backgroundColor: "var(--srb-overlay-bg)" }}
        onClick={onClose}
      />

      <div
        className="srb-relative srb-bg-background srb-border-2 srb-border-secondary srb-w-full srb-max-w-lg srb-max-h-[80vh] srb-overflow-hidden srb-flex srb-flex-col srb-rounded-lg"
        style={{ zIndex: 10002 }}
      >
        {/* Header */}
        <div className="srb-flex srb-items-center srb-justify-between srb-p-4 srb-border-b srb-border-secondary">
          <div className="srb-flex srb-items-center srb-bg-background srb-gap-3">
            <Sliders size={20} className="srb-text-primary" />
            <h3 className="srb-text-lg srb-font-bold srb-text-main srb-m-0">
              Filters
            </h3>
            {activeFilterCount > 0 && (
              <span className="srb-inline-flex srb-items-center srb-justify-center srb-px-2 srb-py-0.5 srb-text-xs srb-font-bold srb-text-white srb-bg-background">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="srb-bg-background srb-border-none srb-text-main hover:srb-text-primary srb-transition srb-cursor-pointer"
          >
            <X size={24} />
          </button>
        </div>

        {/* Filter Content */}
        <div className="srb-flex-1 srb-overflow-y-auto srb-p-4">
          {isLoading ? (
            <div className="srb-flex srb-items-center srb-justify-center srb-py-8">
              <Loader2
                className="srb-animate-spin srb-text-primary"
                size={24}
              />
              <span className="srb-ml-2 srb-text-sm srb-text-muted">
                Loading filters...
              </span>
            </div>
          ) : filterSets.length === 0 ? (
            <p className="srb-text-sm srb-text-muted srb-py-4 srb-text-center">
              No filters available for this category
            </p>
          ) : (
            <div className="srb-space-y-1">
              {filterSets.map((filterSet) => (
                <FilterGroup
                  key={filterSet.param}
                  filterSet={filterSet}
                  selectedValues={selectedFilters[filterSet.title] || []}
                  onSelectFilter={(value) =>
                    onSelectFilter(filterSet.title, value)
                  }
                  onRangeChange={(min, max) =>
                    onRangeChange(filterSet.title, min, max)
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="srb-flex srb-items-center srb-justify-between srb-p-4 srb-border-t srb-border-secondary srb-gap-3">
          <button
            onClick={onClearAll}
            disabled={activeFilterCount === 0}
            className="srb-px-4 srb-py-2 srb-text-sm srb-font-medium srb-text-muted hover:srb-text-primary srb-bg-background srb-border-none srb-cursor-pointer srb-transition disabled:srb-opacity-50 disabled:srb-cursor-not-allowed"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="srb-px-6 srb-py-2 srb-bg-background srb-text-white srb-text-sm srb-font-semibold srb-border-none srb-cursor-pointer hover:srb-opacity-90 srb-transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: ComponentCategory | null;
  onSelectProduct: (product: IProduct) => void;
}

export const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  category,
  onSelectProduct,
}) => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const { filterSets, isLoadingFilters } = useProductFilters(
    isOpen && category ? category.apiName : null,
  );

  useEffect(() => {
    if (isOpen && category) {
      setProducts([]);
      setSearchTerm("");
      setPage(1);
      setHasMore(false);
      setSelectedFilters({});
      setFilterModalOpen(false);
      loadProducts(1, "");

      apiService.trackCategoryBrowsing(category.apiName);
    }
  }, [isOpen, category]);

  const loadProducts = async (
    pageNum: number,
    search: string,
    filterParams?: Record<string, string>,
  ) => {
    if (!category) return;

    setLoading(true);
    try {
      const response = await apiService.getProducts(
        category.apiName,
        pageNum,
        search,
        undefined,
        undefined,
        filterParams,
      );

      if (pageNum === 1) {
        setProducts(response.results);
      } else {
        setProducts((prev) => [...prev, ...response.results]);
      }

      setHasMore(response.next !== null);
      setPage(pageNum);
    } catch (error) {
      console.error("Error loading products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      const params = buildFilterParams(selectedFilters, filterSets);
      loadProducts(1, value, params);
    }
  };

  const handleLoadMore = () => {
    const params = buildFilterParams(selectedFilters, filterSets);
    loadProducts(page + 1, searchTerm, params);
  };

  const handleSelectFilter = (title: string, value: string) => {
    const current = selectedFilters[title] || [];
    const isSelected = current.includes(value);
    const updated = isSelected
      ? current.filter((v) => v !== value)
      : [...current, value];

    const newFilters = { ...selectedFilters };
    if (updated.length === 0) {
      delete newFilters[title];
    } else {
      newFilters[title] = updated;
    }

    setSelectedFilters(newFilters);
    const params = buildFilterParams(newFilters, filterSets);
    loadProducts(1, searchTerm, params);
  };

  const handleRangeChange = (title: string, min: string, max: string) => {
    const newFilters = { ...selectedFilters, [title]: [min, max] };
    setSelectedFilters(newFilters);
    const params = buildFilterParams(newFilters, filterSets);
    loadProducts(1, searchTerm, params);
  };

  const handleRemoveFilter = (title: string, value?: string) => {
    const filterSet = filterSets.find((f) => f.title === title);
    const isRange =
      filterSet?.type === "single_choice" &&
      filterSet.filterItems.some(
        (item) => item.id === "min" || item.id === "max",
      );

    const newFilters = { ...selectedFilters };

    if (!value || filterSet?.type === "boolean_choice" || isRange) {
      delete newFilters[title];
    } else {
      const current = newFilters[title] || [];
      const updated = current.filter((v) => v !== value);
      if (updated.length === 0) {
        delete newFilters[title];
      } else {
        newFilters[title] = updated;
      }
    }

    setSelectedFilters(newFilters);
    const params = buildFilterParams(newFilters, filterSets);
    loadProducts(1, searchTerm, params);
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({});
    loadProducts(1, searchTerm, {});
  };

  const handleSelectProduct = (product: IProduct) => {
    onSelectProduct(product);
    onClose();
  };

  if (!isOpen || !category) return null;

  const getProductPrice = (product: IProduct): number => {
    if (product.prices && product.prices.length > 0) {
      const price = product.prices[0];
      return Number(price.price || 0);
    }
    return Number(product.price || 0);
  };

  const getProductImage = (product: IProduct): string | null => {
    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      const primaryImage =
        product.images.find((img) => img.is_primary) || product.images[0];
      return primaryImage?.image || null;
    }
    return (product as any).image || null;
  };

  // Count active filter groups
  const activeFilterCount = Object.values(selectedFilters).filter(
    (values) => values && values.length > 0,
  ).length;

  // Build filter chips for display
  const filterChips: Array<{
    title: string;
    label: string;
    value?: string;
  }> = [];
  for (const [title, values] of Object.entries(selectedFilters)) {
    if (!values || values.length === 0) continue;
    const filterSet = filterSets.find((f) => f.title === title);
    if (!filterSet) continue;

    const isRange =
      filterSet.type === "single_choice" &&
      filterSet.filterItems.some(
        (item) => item.id === "min" || item.id === "max",
      );

    if (filterSet.type === "boolean_choice") {
      filterChips.push({ title, label: `${filterSet.title}: Yes` });
    } else if (isRange && values.length === 2) {
      const prefix = filterSet.param === "price_range" ? "$" : "";
      filterChips.push({
        title,
        label: `${filterSet.title}: ${prefix}${values[0]} - ${prefix}${values[1]}`,
      });
    } else {
      for (const value of values) {
        filterChips.push({
          title,
          label: `${filterSet.title}: ${value}`,
          value,
        });
      }
    }
  }

  return (
    <div className="srb-fixed srb-inset-0 srb-z-modal srb-flex srb-items-center srb-justify-center srb-p-4">
      <div
        className="srb-absolute srb-inset-0"
        style={{ backgroundColor: "var(--srb-overlay-bg)" }}
        onClick={onClose}
      />

      <div
        className="srb-relative srb-bg-background srb-border-2 srb-border-secondary srb-w-full srb-max-w-4xl srb-max-h-[90vh] srb-overflow-hidden srb-flex srb-flex-col srb-p-6"
        style={(function () {
          const root = document.querySelector(".sim-rig-builder");
          if (!root) return { zIndex: 10000 };
          const borderColor = getComputedStyle(root)
            .getPropertyValue("--srb-secondary-color")
            .trim();
          return { boxShadow: `0 0 0 2px ${borderColor}`, zIndex: 10000 };
        })()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="srb-bg-background srb-border-none srb-absolute srb-top-4 srb-right-4 srb-text-main hover:srb-text-primary srb-transition srb-cursor-pointer"
        >
          <X size={32} />
        </button>

        <div className="srb-w-full srb-flex srb-flex-col srb-overflow-hidden srb-flex-1">
          {/* Header */}
          <div className="srb-flex srb-items-center srb-justify-between srb-mb-4">
            <h2 className="srb-text-2xl srb-font-bold srb-text-main srb-m-0">
              Select {category.name}
            </h2>
          </div>

          {/* Search Bar + Filter Button */}
          <div className="srb-flex srb-items-center srb-gap-3 srb-mb-4">
            <div className="srb-relative srb-flex-1">
              <Search
                className="srb-absolute srb-left-3 srb-top-1/2 srb-transform srb--translate-y-1/2 srb-text-main"
                size={20}
              />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="srb-w-full srb-pl-10 srb-pr-4 srb-py-3 srb-bg-background srb-border srb-border-secondary srb-text-main placeholder:srb-text-muted focus:srb-outline-none focus:srb-border-primary srb-transition"
              />
            </div>
            <button
              onClick={() => setFilterModalOpen(true)}
              className="srb-flex srb-items-center srb-gap-2 srb-px-4 srb-py-3 srb-text-sm srb-font-medium srb-text-main hover:srb-text-primary srb-transition srb-cursor-pointer srb-bg-transparent srb-border srb-border-secondary hover:srb-border-primary srb-flex-shrink-0"
            >
              <Sliders size={18} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="srb-inline-flex srb-items-center srb-justify-center srb-w-5 srb-h-5 srb-text-xs srb-font-bold srb-text-white srb-bg-primary">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="srb-text-sm srb-text-muted hover:srb-text-primary srb-transition srb-cursor-pointer srb-bg-background srb-border-none srb-flex-shrink-0"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Active Filter Chips */}
          {filterChips.length > 0 && (
            <div className="srb-flex srb-flex-wrap srb-gap-2 srb-mb-4">
              {filterChips.map((chip, index) => (
                <span
                  key={`${chip.title}-${chip.value || index}`}
                  className="srb-inline-flex srb-items-center srb-gap-1 srb-px-2 srb-py-1 srb-text-xs srb-bg-hover srb-border srb-border-primary srb-text-main srb-rounded-md"
                >
                  {chip.label}
                  <button
                    onClick={() => handleRemoveFilter(chip.title, chip.value)}
                    className="srb-ml-1 srb-text-primary srb-cursor-pointer srb-bg-background srb-border-none srb-p-0 srb-leading-none"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Products List */}
          <div className="srb-flex-1 srb-overflow-y-auto srb-pr-2 srb-space-y-3 srb-min-h-0 srb-pb-2 srb-relative">
            {/* Loading overlay when filtering/searching with existing products */}
            {loading && products.length > 0 && (
              <div
                className="srb-absolute srb-inset-0 srb-z-10 srb-flex srb-items-start srb-justify-center srb-pt-4"
                style={{
                  backgroundColor: "var(--srb-background-color)",
                  opacity: 0.95,
                }}
              >
                <div className="srb-w-full srb-px-2">
                  <ProductSkeletonList count={4} />
                </div>
              </div>
            )}

            {loading && products.length === 0 ? (
              <ProductSkeletonList count={6} />
            ) : products.length === 0 ? (
              <div className="srb-text-center srb-py-12">
                <p className="srb-text-muted">No products found</p>
              </div>
            ) : (
              <>
                {" "}
                {products.map((product) => {
                  const price = getProductPrice(product);
                  const image = getProductImage(product);

                  return (
                    <button
                      key={product.id}
                      onClick={() => handleSelectProduct(product)}
                      className="srb-cursor-pointer srb-w-full srb-p-4 srb-bg-background hover:srb-bg-hover srb-border srb-border-secondary hover:srb-border-primary srb-transition srb-text-left srb-group"
                    >
                      <div className="srb-flex srb-gap-4">
                        {/* Product Image */}
                        {image && (
                          <div className="srb-relative srb-w-20 srb-h-20 srb-flex-shrink-0 srb-bg-white srb-overflow-hidden">
                            <img
                              src={image}
                              alt={product.name}
                              className="srb-w-full srb-h-full srb-object-contain"
                            />
                          </div>
                        )}

                        {/* Product Info */}
                        <div className="srb-flex-1 srb-min-w-0">
                          <div className="srb-font-semibold srb-text-main srb-transition srb-truncate">
                            {product.name}
                          </div>
                          <div className="srb-text-sm srb-text-muted">
                            {product.brand?.name ||
                              (product as any).manufacturer ||
                              "Unknown"}
                          </div>
                          {product.description && (
                            <div className="srb-text-xs srb-text-muted srb-mt-1 srb-line-clamp-2">
                              {product.description}
                            </div>
                          )}
                          {/* Discount Info */}
                          {(product.discount_code ||
                            product.discount_amount) && (
                            <div className="srb-mt-2 srb-flex srb-flex-wrap srb-gap-2">
                              {product.discount_amount && (
                                <span className="srb-inline-block srb-text-green-400 srb-text-xs srb-font-semibold">
                                  {product.discount_amount}
                                </span>
                              )}
                              {product.discount_code && (
                                <span className="srb-inline-block srb-text-primary srb-text-xs srb-font-mono">
                                  Code: {product.discount_code}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Price */}
                        <div className="srb-flex srb-flex-col srb-items-end srb-justify-center srb-flex-shrink-0">
                          <div className="srb-text-lg srb-font-bold srb-text-main">
                            ${price.toFixed(2)}
                          </div>
                          {product.availability && (
                            <div className="srb-text-xs srb-text-muted srb-capitalize">
                              {product.availability === "in_stock"
                                ? "In Stock"
                                : product.availability === "out_of_stock"
                                  ? "Out of Stock"
                                  : "Preorder"}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {/* Load More Button */}
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="srb-cursor-pointer srb-w-full srb-p-4 srb-bg-hover srb-flex srb-items-center srb-justify-center srb-text-main srb-font-semibold hover:srb-bg-opacity-50 srb-border srb-border-secondary hover:srb-border-primary srb-transition srb-text-left srb-group disabled:srb-opacity-50 disabled:srb-cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="srb-flex srb-items-center srb-justify-center srb-gap-2">
                        <Loader2 className="srb-animate-spin" size={20} />
                        <span>Loading...</span>
                      </div>
                    ) : (
                      "Load More"
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filterSets={filterSets}
        isLoading={isLoadingFilters}
        selectedFilters={selectedFilters}
        onSelectFilter={handleSelectFilter}
        onRangeChange={handleRangeChange}
        onClearAll={handleClearAllFilters}
        activeFilterCount={activeFilterCount}
      />
    </div>
  );
};
